import type { FolderDto, FolderKind } from '@tms/shared';
import { ChevronDown, ChevronRight, FolderPlus, Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button, Menu, MenuItem, Skeleton } from '../../components/ui';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { usePermissions } from '../auth/permissions';
import { DeleteFolderDialog } from './DeleteFolderDialog';
import { FolderFormDialog } from './FolderFormDialog';
import { useFolderTree } from './api';

export type FolderSelection = string | 'all';

export interface FolderTreeProps {
  projectCode: string;
  selected: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  /** Rows of the list beside the tree, for the "everything" entry. */
  total: number;
  /**
   * Which tree to show. A project has one per kind of content, and they never
   * mix: checklist folders hold only checklists, case folders only test cases.
   */
  kind: FolderKind;
  /** Label of the "everything" entry, named after that content. */
  allLabel: string;
}

/** Folder sidebar of a project screen: selection plus folder management. */
export const FolderTree = ({
  projectCode,
  selected,
  onSelect,
  total,
  kind,
  allLabel,
}: FolderTreeProps) => {
  const { t } = useT();
  const { can } = usePermissions();
  const { data: folders, isPending } = useFolderTree(projectCode, kind);

  const [createOpen, setCreateOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [editing, setEditing] = useState<FolderDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FolderDto | null>(null);

  return (
    <aside className="w-full shrink-0 lg:w-64" aria-label={t('folders.title')}>
      <div className="rounded-xl border border-border bg-surface">
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <h2 className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">
            {t('folders.title')}
          </h2>
          {can('case:write') ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCreateParentId(null);
                setCreateOpen(true);
              }}
              aria-label={t('folders.new')}
            >
              <Plus className="size-3.5" />
            </Button>
          ) : null}
        </div>

        <div className="p-1.5">
          <FolderRow
            label={allLabel}
            count={total}
            active={selected === 'all'}
            depth={0}
            onClick={() => onSelect('all')}
            icon={<Layers className="size-3.5" />}
          />
          {isPending ? (
            <div className="flex flex-col gap-1 p-1.5">
              <Skeleton className="h-7 w-full" />
              <Skeleton className="h-7 w-full" />
              <Skeleton className="h-7 w-full" />
            </div>
          ) : (folders ?? []).length === 0 ? (
            <p className="px-2 py-3 text-[12px] text-ink-subtle">
              {kind === 'case'
                ? t('folders.empty')
                : kind === 'checklist'
                  ? t('folders.emptyChecklists')
                  : t('folders.emptyRuns')}
            </p>
          ) : (
            (folders ?? []).map((folder) => (
              <FolderBranch
                key={folder.id}
                folder={folder}
                depth={0}
                selected={selected}
                onSelect={onSelect}
                canWrite={can('case:write')}
                canDelete={can('case:delete')}
                onAddChild={(parentId) => {
                  setCreateParentId(parentId);
                  setCreateOpen(true);
                }}
                onEdit={setEditing}
                onDelete={setPendingDelete}
              />
            ))
          )}
        </div>
      </div>

      <FolderFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        projectCode={projectCode}
        kind={kind}
        defaultParentId={createParentId}
      />

      {editing ? (
        <FolderFormDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          projectCode={projectCode}
          kind={kind}
          folder={{ id: editing.id, name: editing.name, parentId: editing.parentId }}
        />
      ) : null}

      <DeleteFolderDialog
        projectCode={projectCode}
        kind={kind}
        folder={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={(folder) => {
          if (selected === folder.id) onSelect('all');
        }}
      />
    </aside>
  );
};

const FolderRow = ({
  label,
  count,
  active,
  depth,
  onClick,
  icon,
  expander,
  actions,
}: {
  label: string;
  count?: number;
  active: boolean;
  depth: number;
  onClick: () => void;
  icon?: React.ReactNode;
  expander?: React.ReactNode;
  actions?: React.ReactNode;
}) => (
  <div
    className={cn(
      'group flex items-center gap-1 rounded-md pr-1',
      active ? 'bg-accent-soft' : 'hover:bg-sunken',
    )}
    style={{ paddingLeft: `${depth * 12}px` }}
  >
    {expander ?? <span className="w-5" />}
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left text-[13px]',
        active ? 'font-medium text-accent-fg' : 'text-ink',
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
      {count !== undefined ? (
        <span className="ml-auto shrink-0 pl-1 text-[12px] tabular-nums text-ink-subtle">
          {count}
        </span>
      ) : null}
    </button>
    {actions ? <span className="opacity-0 transition-opacity group-hover:opacity-100">{actions}</span> : null}
  </div>
);

const FolderBranch = ({
  folder,
  depth,
  selected,
  onSelect,
  canWrite,
  canDelete,
  onAddChild,
  onEdit,
  onDelete,
}: {
  folder: FolderDto;
  depth: number;
  selected: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  canWrite: boolean;
  canDelete: boolean;
  onAddChild: (parentId: string) => void;
  onEdit: (folder: FolderDto) => void;
  onDelete: (folder: FolderDto) => void;
}) => {
  const { t } = useT();
  const [expanded, setExpanded] = useState(true);
  const hasChildren = folder.children.length > 0;

  return (
    <>
      <FolderRow
        label={folder.name}
        count={folder.totalCount}
        active={selected === folder.id}
        depth={depth}
        onClick={() => onSelect(folder.id)}
        expander={
          hasChildren ? (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-label={
                expanded
                  ? t('folders.collapse', { name: folder.name })
                  : t('folders.expand', { name: folder.name })
              }
              aria-expanded={expanded}
              className="grid size-5 place-items-center rounded text-ink-subtle hover:text-ink"
            >
              {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            </button>
          ) : undefined
        }
        actions={
          canWrite ? (
            <Menu>
              <MenuItem onSelect={() => onAddChild(folder.id)}>
                <FolderPlus className="size-3.5" />
                {t('folders.newSubfolder')}
              </MenuItem>
              <MenuItem onSelect={() => onEdit(folder)}>
                <Pencil className="size-3.5" />
                {t('folders.rename')}
              </MenuItem>
              {canDelete ? (
                <MenuItem destructive onSelect={() => onDelete(folder)}>
                  <Trash2 className="size-3.5" />
                  {t('folders.delete')}
                </MenuItem>
              ) : null}
            </Menu>
          ) : null
        }
      />
      {expanded
        ? folder.children.map((child) => (
            <FolderBranch
              key={child.id}
              folder={child}
              depth={depth + 1}
              selected={selected}
              onSelect={onSelect}
              canWrite={canWrite}
              canDelete={canDelete}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))
        : null}
    </>
  );
};
